
import { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === '/';
  const { t } = useTranslation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { path: '/', label: t('nav.home') },
    { path: '/about', label: t('nav.about') },
    { path: '/services', label: t('nav.services') },
    { path: '/calendar', label: t('nav.calendar') },
    { path: '/sermons', label: t('nav.sermons') },
    { path: '/resources', label: t('nav.resources') },
    { path: '/news', label: t('nav.news') },
    { path: '/contact', label: t('nav.contact') },
  ];

  const showTransparent = isHome && !scrolled;

  const handleNavClick = useCallback((path: string) => {
    if (location.pathname === path) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [location.pathname]);

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${showTransparent ? 'bg-transparent' : 'bg-white shadow-md'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3 flex-shrink-0">
          <img 
            src="/assets/readdy/5b2ce4067850f11889f7445dd031c400-4ee2c140aa40.png" 
            alt="Saint Mina & Pope Cyril VI Church Logo" 
            className="h-9 sm:h-12 w-auto"
          />
        </Link>

        {/* Desktop Navigation */}
        <div className="hidden lg:flex items-center gap-6">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              onClick={() => handleNavClick(link.path)}
              className={`text-sm font-medium transition-colors hover:text-gold-500 ${
                location.pathname === link.path ? 'text-gold-500' : showTransparent ? 'text-white' : 'text-gray-800'
              }`}
            >
              {link.label}
            </Link>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <LanguageSwitcher isTransparent={showTransparent} />
          
          <Link 
            to="/donate" 
            className="px-4 sm:px-6 py-2 sm:py-2.5 bg-[#3d1a1e] text-white text-xs sm:text-sm font-medium rounded-full hover:bg-[#2a1215] transition-colors whitespace-nowrap cursor-pointer"
          >
            {t('nav.donate')}
          </Link>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className={`lg:hidden w-10 h-10 flex items-center justify-center rounded-lg cursor-pointer ${showTransparent ? 'text-white' : 'text-gray-800'}`}
          >
            <i className={`text-2xl ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden bg-white border-t border-gray-100 shadow-lg">
          <div className="px-6 py-4 space-y-2">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => {
                  setMobileMenuOpen(false);
                  handleNavClick(link.path);
                }}
                className={`block py-3 text-sm font-medium transition-colors hover:text-gold-500 ${
                  location.pathname === link.path ? 'text-gold-500' : 'text-gray-800'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>
        </div>
      )}
    </nav>
  );
}
